import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HeaderComponent } from '../../../shared/header/header.component';
import { CashRegisterService } from '../../../../services/cash-register/cash-register.service';
import { PaymentService } from '../../../../services/payment/payment.service';
import { AtendimentoService } from '../../../../services/atendimento/atendimento.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { CashRegister } from '../../../../interfaces/cash-register';
import { CostCenter } from '../../../../interfaces/payment';
import { PacienteBusca } from '../../../../interfaces/dados-pagamento';
import { brandNameOrDefault, formatBRL, formatDateTimeBR, openPrintWindow, receiptLogoHtml } from '../../../../utils/print-receipt.util';
import { BrandService } from '../../../../services/brand/brand.service';

@Component({
  selector: 'app-estacionamento',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, RouterLink],
  templateUrl: './estacionamento.component.html',
})
export class EstacionamentoComponent implements OnInit {
  caixas: CashRegister[] = [];
  caixa: CashRegister | null = null;
  valor: number | null = null;

  // Typeahead pessoa física
  pessoaTermo = '';
  pessoaResultados: PacienteBusca[] = [];
  pessoaId: number | null = null;
  pessoaNome = '';
  isSearchingPessoa = false;
  showPessoaDropdown = false;
  private pessoaTimer: any = null;

  // Centro de custo (dropdown)
  centrosCusto: CostCenter[] = [];
  centroCustoSelecionado: CostCenter | null = null;
  isLoadingCentros = false;

  observacao: string = 'ESTACIONAMENTO';
  dataTransacao: string = '';
  erro: string | null = null;
  isSubmitting = false;
  sucesso: { nrSequencia: number; valor: number } | null = null;
  private username = '';
  private clinicId = 0;

  constructor(
    private router: Router,
    private cashRegisterService: CashRegisterService,
    private paymentService: PaymentService,
    private atendimentoService: AtendimentoService,
    private localStorage: LocalStorageService,
    private brandService: BrandService,
  ) {}

  async ngOnInit() {
    this.username = this.localStorage.get('login') as string;
    this.clinicId = Number(this.localStorage.get('currentUnit'));

    if (!this.username || !this.clinicId) {
      this.router.navigate(['/home']);
      return;
    }

    this.caixas = await this.cashRegisterService.getByUnit(this.clinicId, this.username);
    this.caixa = this.caixas[0] ?? null;
    this.dataTransacao = this.nowLocalISO();
    await this.carregarCentrosCusto();
  }

  async onCaixaChange() {
    this.centroCustoSelecionado = null;
    await this.carregarCentrosCusto();
  }

  private async carregarCentrosCusto() {
    if (!this.caixa) return;
    this.isLoadingCentros = true;
    try {
      this.centrosCusto = await this.paymentService.getCostCenters(this.caixa.establishmentId);
    } finally {
      this.isLoadingCentros = false;
    }
  }

  onPessoaTermoChange() {
    if (this.pessoaTimer) clearTimeout(this.pessoaTimer);
    this.pessoaId = null;
    this.pessoaNome = '';

    const termo = this.pessoaTermo.trim();
    if (termo.length < 3) {
      this.pessoaResultados = [];
      this.showPessoaDropdown = false;
      return;
    }

    this.pessoaTimer = setTimeout(async () => {
      this.isSearchingPessoa = true;
      try {
        this.pessoaResultados = await this.atendimentoService.buscarPaciente(termo);
        this.showPessoaDropdown = this.pessoaResultados.length > 0;
      } finally {
        this.isSearchingPessoa = false;
      }
    }, 400);
  }

  selecionarPessoa(p: PacienteBusca) {
    this.pessoaId = p.cdPessoaFisica;
    this.pessoaNome = p.nmPessoaFisica;
    this.pessoaTermo = `${p.cdPessoaFisica} — ${p.nmPessoaFisica}`;
    this.showPessoaDropdown = false;
    this.pessoaResultados = [];
  }

  onObservacaoFocus() {
    if (this.observacao === 'ESTACIONAMENTO') this.observacao = '';
  }

  formValido(): boolean {
    return (
      !!this.caixa &&
      !!this.valor &&
      this.valor > 0 &&
      !!this.pessoaId &&
      !!this.centroCustoSelecionado
    );
  }

  async registrar() {
    if (!this.formValido() || this.isSubmitting) return;

    this.isSubmitting = true;
    this.erro = null;
    try {
      const result = await this.paymentService.postParkingPayment({
        nrSeqCaixa: this.caixa!.sequence,
        vlPagamento: this.valor!,
        cdPessoaFisica: this.pessoaId!,
        cdCentroCusto: this.centroCustoSelecionado!.cdCentroCusto,
        dsObservacao: this.observacao?.trim() || undefined,
        nmUsuario: this.username,
        cdEstabelecimento: this.caixa!.establishmentId,
      });
      this.sucesso = { nrSequencia: result.nrSequencia, valor: this.valor! };
    } catch (e: any) {
      this.erro = e?.error?.feedback || e?.message || 'Erro ao registrar estacionamento.';
    } finally {
      this.isSubmitting = false;
    }
  }

  imprimirRecibo() {
    if (!this.sucesso || !this.caixa) return;
    const html = `
      <div class="header">
        <div class="logo">${receiptLogoHtml(this.brandService.logo, this.brandService.cdEmpresa)}</div>
        <div style="text-align: right;">
          <p class="title">Recibo — Estacionamento</p>
          <p class="subtitle">Recebimento médico</p>
          <p class="receipt-number">Nº ${this.sucesso.nrSequencia}</p>
        </div>
      </div>
      <table class="data">
        <tr><td class="label">Caixa</td><td class="value">${this.caixa.name} — ${this.caixa.establishmentName}</td></tr>
        <tr><td class="label">Pessoa Física</td><td class="value">${this.pessoaNome} <small style="color: #626272">(${this.pessoaId})</small></td></tr>
        <tr><td class="label">Centro de Custo</td><td class="value">${this.centroCustoSelecionado?.dsCentroCusto || ''} <small style="color: #626272">(${this.centroCustoSelecionado?.cdCentroCusto || ''})</small></td></tr>
        <tr><td class="label">Data/Hora</td><td class="value">${formatDateTimeBR(this.dataTransacao || new Date())}</td></tr>
        <tr><td class="label">Usuário</td><td class="value">${this.username}</td></tr>
      </table>
      <div class="valor-box">
        <div style="font-size: 10px; color: #626272;">Valor recebido</div>
        <div class="amount">${formatBRL(this.sucesso.valor)}</div>
      </div>
      <div class="obs-box">
        <div class="label">Observação</div>
        <div>${this.observacao || '—'}</div>
      </div>
      <div class="signature">
        <div class="line"></div>
        <div class="name">Assinatura do responsável</div>
      </div>
      <div class="footer">${brandNameOrDefault(this.brandService.brandName)} — Comprovante sem valor fiscal</div>
    `;
    openPrintWindow(`Estacionamento ${this.sucesso.nrSequencia}`, html);
  }

  novoRegistro() {
    this.sucesso = null;
    this.valor = null;
    this.pessoaId = null;
    this.pessoaNome = '';
    this.pessoaTermo = '';
    this.pessoaResultados = [];
    this.showPessoaDropdown = false;
    this.centroCustoSelecionado = null;
    this.observacao = 'ESTACIONAMENTO';
    this.dataTransacao = this.nowLocalISO();
    this.erro = null;
  }

  voltar() {
    this.router.navigate(['/lancamentos-financeiros']);
  }

  formatCurrency(value: number): string {
    return value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? 'R$ 0,00';
  }

  private nowLocalISO(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
